/**
 * A component that tells the child component:
 *
 *  - how much of the child is in view
 *  - the vertical position of the child, potentially relative to the initial
 *    position (useful for parallax!)
 */

import * as React from "react";
import {findDOMNode} from "react-dom";

import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";

type OwnProps = {
    // Render a parallax-aware child.
    //
    // Note that `position` can lie. If this component is in the browser
    // viewport when we first render, `position` will be relative to the
    // initial position. Otherwise, `position` is the position relative to
    // the center of the screen.
    //
    // In the later case, `position` is a number between -1.5 (meaning the
    // center of the object is at least one and a half screens above the
    // center of the viewport) and 1.5 (meaning the center of the object is
    // one and a half screens below the center of the viewport).
    children: (data: {pctInView: number; position: number}) => React.ReactNode;
};

type Props = OwnProps & WithActionSchedulerProps;

type State = {
    pctInView: number;
    position: number;
    initialPosition: number;
};

class InView extends React.Component<Props, State> {
    state: State = {
        pctInView: 0.0,
        position: 0.0,
        // We render something before we are sure about our position. This is
        // because we need to mount something before calculating our position,
        // both on the client and on the server.
        initialPosition: NaN,
    };

    componentDidMount() {
        window.addEventListener("scroll", this._scheduleCheckIfInView);
        // Usually, the scroll checker will help us out. We have a timer in
        // case the viewport content changes for some other reason (e.g.,
        // reflow).
        this.props.schedule.interval(this._checkIfInView, 1000);
    }

    componentWillUnmount() {
        this._unmounted = true;
        window.removeEventListener("scroll", this._scheduleCheckIfInView);
    }

    _scheduleCheckIfInView: () => void = () => {
        // TODO(jeff, MP-2132): Use Wonder Blocks Timing API
        // eslint-disable-next-line no-restricted-syntax
        requestAnimationFrame(this._checkIfInView);
    };

    _unmounted = false;

    _checkIfInView: () => void = () => {
        if (this._unmounted) {
            return;
        }
        const el = findDOMNode(this);
        let pctInView: number;
        let position: number;
        if (el && el instanceof Element) {
            const rect = el.getBoundingClientRect();
            const pctAboveTop = Math.max(0, -rect.top / rect.height);
            const pctBelowBottom = Math.max(
                0,
                (rect.bottom - window.innerHeight) / rect.height,
            );

            // Usually, pctInView is the percent of the child that is visible.
            // However, if the object is larger than the viewport, we instead
            // calculate the percent of the viewport that is covered by the
            // child.
            const highestPossiblePctInView = Math.min(
                1.0,
                1.0 - (rect.height - window.innerHeight) / rect.height,
            );

            pctInView = Math.min(
                1.0,
                Math.max(0.0, 1.0 - pctAboveTop - pctBelowBottom) /
                    highestPossiblePctInView,
            );

            // position is the y-position of the center of the child, where
            // -1.0 means the center of the child is at the top of the window,
            // and 1.0 means the center of the child is at the bottom of the
            // window. Clamp the value between -1.5 and 1.5 so that we don't
            // cause needless updates when we're out of view.
            position = Math.max(
                -1.5,
                Math.min(
                    1.5,
                    (rect.top + rect.bottom) / 2 / window.innerHeight - 0.5,
                ),
            );
        } else {
            pctInView = 0.0;
            position = 0;
        }

        if (
            this.state.pctInView !== pctInView ||
            this.state.position !== position
        ) {
            this.setState({
                pctInView,
                position,
                initialPosition: 0,
            });
        }
    };

    render(): React.ReactNode {
        const {pctInView, position, initialPosition} = this.state;
        return this.props.children({
            pctInView,
            position: position - (initialPosition || 0),
        });
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof InView,
        React.ComponentProps<typeof InView>
    >
>;
export default withActionScheduler(InView) as React.ComponentType<ExportProps>;
